import { type DraggableAttributes, type DraggableSyntheticListeners } from "@dnd-kit/core"
import { IconGripVertical } from "@tabler/icons-react"
import { ComponentType, type CSSProperties, FC, type ReactNode } from "react"

import Tooltip from "@forento/ui/components/Tooltip"
import { cn, tw } from "@forento/ui/utilities/tailwind"

import Link from "~/components/Link"

const actionClass = tw`hover:bg-primary/8 hover:text-primary focus-visible:outline-primary relative flex cursor-pointer items-center gap-1 rounded-lg p-2 text-slate-400 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2`

type Props = {
	ref?: (element: HTMLDivElement) => void
	title: string
	sticker?: string
	subtitle?: ReactNode
	thumbnail?: ReactNode
	href?: string
	actions?: ({
		id: string
		tooltip: string
		icon: ComponentType<{ className: string }>
		notification?: boolean
		number?: number
	} & (
		| { onClick: () => void; href?: never; disabled?: boolean; danger?: boolean }
		| { href: string; onClick?: never }
	))[]
	dragHandle?: { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners }
	style?: CSSProperties
	className?: string
}
const ProductListRow: FC<Props> = ({
	ref,
	title,
	sticker,
	subtitle,
	thumbnail,
	href,
	actions,
	dragHandle,
	style,
	className,
}) => (
	<div
		ref={ref}
		style={style}
		className={cn(
			"border-component-border bg-component-background hover:border-primary/20 flex flex-wrap items-center gap-3 rounded-3xl border p-4 transition-colors duration-150",
			className,
		)}
	>
		{dragHandle && (
			<div
				{...dragHandle.attributes}
				{...dragHandle.listeners}
				className="shrink-0 cursor-grab text-slate-300 transition-colors hover:text-slate-500 active:cursor-grabbing"
			>
				<IconGripVertical className="size-4" />
			</div>
		)}

		{thumbnail && <div className="w-20 shrink-0 overflow-hidden rounded-xl sm:w-40 lg:w-56">{thumbnail}</div>}

		<div className="flex min-w-0 flex-[1_1_120px] flex-col gap-0.5">
			{href ? (
				<Link to={href} className="hover:text-primary truncate font-semibold text-slate-900">
					{title}
				</Link>
			) : (
				<div className="truncate font-semibold text-slate-900">{title}</div>
			)}

			{(sticker || subtitle) && (
				<div className="flex items-center gap-2">
					{sticker && (
						<span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 uppercase">
							{sticker}
						</span>
					)}
					{subtitle && <div className="text-light flex items-center gap-2 text-xs">{subtitle}</div>}
				</div>
			)}
		</div>

		{actions && (
			// Wraps on narrow screens rather than pushing the row, and the whole page, wider than the viewport.
			<div className="flex min-w-0 flex-wrap items-center gap-1 transition-opacity duration-150">
				{actions.map(action => {
					const children = (
						<>
							{action.notification && (
								<div className="absolute top-1 right-1 size-2 rounded-full bg-red-500" />
							)}
							<action.icon className="size-4" />
							{action.number && <div className="text-xs">{action.number}</div>}
						</>
					)
					return (
						<Tooltip key={action.id} tooltip={action.tooltip}>
							{action.onClick ? (
								action.disabled ? (
									<div className="relative cursor-not-allowed rounded-lg p-2 text-slate-200">
										{children}
									</div>
								) : (
									<button
										type="button"
										onClick={action.onClick}
										aria-label={action.tooltip}
										className={cn(
											actionClass,
											action.danger && "hover:bg-red-50 hover:text-red-500",
										)}
									>
										{children}
									</button>
								)
							) : action.href.startsWith("/") && !action.href.startsWith("//") ? (
								<Link to={action.href} aria-label={action.tooltip} className={actionClass}>
									{children}
								</Link>
							) : (
								<a
									href={action.href}
									target="_blank"
									rel="noreferrer noopener"
									aria-label={`${action.tooltip} (opens in a new tab)`}
									className={actionClass}
								>
									{children}
								</a>
							)}
						</Tooltip>
					)
				})}
			</div>
		)}
	</div>
)

export default ProductListRow
